import { Type } from '@angular/core';
import { Routes } from '@angular/router';

/**
 * A seasonal homepage that replaces HomeComponent at "/" for a date window.
 * Times are UTC milliseconds; write them as Pacific midnights (PDT = UTC-7, PST = UTC-8).
 */
export interface SeasonalHome {
  name: string;
  /** First moment the seasonal homepage is shown (inclusive). */
  start: number;
  /** First moment it is no longer shown (exclusive). */
  end: number;
  load: () => Promise<Type<unknown>>;
  /** Optional header logo, shown only on "/" while this season is live. */
  logo?: string;
}

// Add new seasons here. Windows must not overlap (the spec checks this).
export const SEASONAL_HOMES: readonly SeasonalHome[] = [
  {
    // Mon 2026-10-26 00:00 PDT through Sun 2026-11-01 23:59 PST (DST ends Nov 1).
    name: 'halloween',
    start: Date.UTC(2026, 9, 26, 7),
    end: Date.UTC(2026, 10, 2, 8),
    load: () => import('../home-halloween/home-halloween.component').then(c => c.HomeHalloweenComponent),
    logo: 'https://services.ifa.com/art/images/3763_144x144_c_halloween-logo_q90.webp',
  },
  {
    // Fri 2026-11-06 00:00 PST through Sun 2026-11-29 23:59 PST.
    name: 'thanksgiving',
    start: Date.UTC(2026, 10, 6, 8),
    end: Date.UTC(2026, 10, 30, 8),
    load: () => import('./home-thanksgiving/home-thanksgiving.component').then(c => c.HomeThanksgivingComponent),
  },
];

export function isSeasonActive(season: SeasonalHome, now: number = Date.now()): boolean {
  return now >= season.start && now < season.end;
}

export function activeSeasonalHome(now: number = Date.now()): SeasonalHome | null {
  return SEASONAL_HOMES.find(season => isSeasonActive(season, now)) ?? null;
}

/**
 * One "/" route per season, each gated by canMatch. Place these before the default HomeComponent route.
 * canMatch is re-checked on every navigation, so the switch happens on schedule even on a long-running
 * SSR server; a single loadComponent that picks by date would be cached by the router after its first load.
 */
export function seasonalHomeRoutes(): Routes {
  return SEASONAL_HOMES.map(season => ({
    path: '',
    pathMatch: 'full' as const,
    canMatch: [() => isSeasonActive(season)],
    loadComponent: season.load,
  }));
}